import useSelector from "src/utils/useSelector";
import { AtOutboundEventActions, Details, OutboundConsumer } from "src/components/AccuTrade/accuTradeHelpers";
import { Source, TradeIn } from "Types/TradeIn";
import { Unit } from "@carsdotcom/mti-sdk";
import { patchTradeInRequest } from "src/store/actions/tradeIn";
import { getDealUUID } from "src/store/selectors/deal";
import { Person } from "Types/api/Person";
import { patchDealPerson } from "src/store/dataService/actions/deal";
import { useAccuTradeListener } from "src/components/AccuTrade/hooks/useAccuTradeListener";
import { useDispatch } from "react-redux";

const useAccuTradeChangeHandler = (window: Window, started?: () => void, finished?: () => void) => {
    const uuid = useSelector(getDealUUID);
    const dispatch = useDispatch();

    const patchTrade = (atDetails: Details) => {
        const { vehicle, rawData, price } = atDetails;

        const tradeIn: TradeIn = {
            year: vehicle.year.toString(),
            make: vehicle.make,
            model: vehicle.model,
            trim: vehicle.style,
            vin: vehicle.vin || undefined,
            ...(rawData.plate_number &&
                rawData.plate_state && {
                    plate: {
                        value: rawData.plate_number,
                        state: rawData.plate_state,
                    },
                }),
            odometer: {
                amount: Number(vehicle.mileage),
                unit: Unit.mi,
            },
            balance: +rawData.loan_owed_amount || undefined,
            offer_uuid: atDetails.offer,
            offer_expiration: atDetails.expirationDate,
            value: { target: price.offer, high: price.offer, low: price.offer },
            source: Source.AccuTradeICO,
        };

        if (uuid) {
            dispatch(patchTradeInRequest(tradeIn, uuid));
        }
    };

    const patchPerson = (atConsumer: OutboundConsumer) => {
        const person: Person = {
            ...(atConsumer.first_name && { first_name: atConsumer.first_name }),
            ...(atConsumer.last_name && { last_name: atConsumer.last_name }),
            ...(atConsumer.email && { email: atConsumer.email }),
            ...(atConsumer.postal_code && { zip: atConsumer.postal_code }),
            ...(atConsumer.cell_phone && {
                mobile_phone: atConsumer.cell_phone,
                preferences: { method_of_contact: "sms", tcpa_consent: true },
            }),
            ...(atConsumer.phone_verified === true && { mobile_phone_verified: atConsumer.phone_verified }),
        };

        if (uuid) {
            dispatch(patchDealPerson(uuid, person));
        }
    };

    useAccuTradeListener(window, (data) => {
        if (data?.atOutbound?.event?.eventAction) {
            const { eventAction } = data.atOutbound.event;

            started && eventAction === AtOutboundEventActions.tradeStart && started();
            finished && eventAction === AtOutboundEventActions.tradeComplete && finished();
        }

        if (data?.atOutbound?.estimateComplete) {
            const consumer = data.atOutbound.details.rawData.consumer;
            // if you return to a trade flow that already has a consumer info, the consumer comes back as a number
            typeof consumer === "object" && patchPerson(consumer);
            patchTrade(data.atOutbound.details);
        }

        if (
            data?.atOutbound?.type === "dataChange" &&
            typeof data.atOutbound?.details?.rawData?.consumer === "object" &&
            data.atOutbound?.details?.rawData?.consumer?.phone_verified
        ) {
            patchPerson(data.atOutbound.details.rawData.consumer);
        }
    });
};

export default useAccuTradeChangeHandler;
